

import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator"
import { Getter, Mutation, Action, State } from "vuex-class"

import { LoginResultTenant } from "@/types"
import User, { CurrentUser, SystemUser } from "@/types/user"
import { CasesNotification, notificationFunctions, Urgency } from "@/types/notification"

import api from "@/services/api"
import tenantApi from "@/services/api_Tenant"

import { BDropdown } from "bootstrap-vue"
import moment, { normalizeUnits } from "moment"
import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import LightDarkSwitch from "./Account/LightDarkSwitch.vue"
import UserSettingsModal from "@/components/UserSettingsModal.vue"
import { Subscription } from "@/types/subscription"
import SetStatusModal from "@/components/SetStatusModal.vue"
import TenantInfoDisplay from "@/components/Info/TenantInfoDisplay.vue"
import TimeZoneSelect from "@/components/New/Page/TimeZoneSelect.vue"

import Guid from "@/scripts/guid"
import NewInterfaceSwitch from "@/components/Account/NewInterfaceSwitch.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { UserSettings } from "@/types/userSettings"

@Component({
	components: {
		"new-interface-switch": NewInterfaceSwitch,
		"v-select": vSelect,
		"light-dark": LightDarkSwitch,
		"scrollbar": VuePerfectScrollbar,
		"user-settings-modal": UserSettingsModal,
		"set-status-modal": SetStatusModal,
		"info-display": TenantInfoDisplay,
		"timezone-select": TimeZoneSelect
	}
})
export default class NavHeader extends Vue {
	$refs!: {
		notificationsDropdown: BDropdown,
		userSettingsModal: UserSettingsModal,
		setStatusModal: SetStatusModal
	}

	@Prop(String)
	title!: string;

	@State
	public userSettings!: UserSettings | null

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getScreenWidth") public screenWidth!: number;

	@State public systemUser!: SystemUser
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getSubscription") subscription?: Subscription
	@Getter("hasSubscription") hasSubscription!: boolean

	@Getter("getNotifications") public currentNotifications!: CasesNotification[];
	@Getter("getBrowserNotificationsPermission") public browserNotificationsEnabled!: boolean;
	@Getter("getHighestUrgency") public highestUrgency!: Urgency | null

	@Getter("getRecordName") getRecordName!: string;

	@Mutation("setSearch") setSearch!: (search: string) => void;

	@Mutation
	private setManualLogout!: (manualLogout: boolean) => void

	@Action setTheme!: (theme: string) => void;
	@Action requestBrowserNotificationsPermission!: () => void
	@Action processNotification!: (notification: CasesNotification) => void
	@Action clearUserNotifications!: () => void
	@Action markLocalNotificationsAsSent!: () => void

	@Action("selectTenant")
	public loginSelectTenant!: (tenant: LoginResultTenant) => Promise<void>

	@Action("logOut")
	public logOut!: () => void;

	public search!: string;
	public searchResults: any[] = [];
	public selectedItem: any = null;

	private pictureError: boolean = false;
	private pictureGuid: string = Guid.newGuid();

	private debounceSearch: NodeJS.Timeout | null = null;

	public notificationTimePeriods: string[] = [];

	infoShown: boolean = false

	public get isNotificationActionsEnabled () {
		return this.getIsFeatureEnabled(TenantFeatures.NotificationActions)
	}

	private get timeZoneSwitchEnabled (): boolean {
		return !!this.me?.tenantEnabledFeatures?.includes(TenantFeatures.UiTimezone)
	}

	public get viewSearchResults (): any[] {
		return this.searchResults
	}

	@Watch("currentNotifications")
	private currentNotificationsChanged (newNotifications: CasesNotification[]) {
		this.updateNotificationTimePeriods()
	}

	public get myPictureUrl (): string {
		let imageUrl = ""

		if (this.pictureError || (!this.me && !this.systemUser)) {
			imageUrl = "images/blank_user.png"
		} else {
			if (this.systemUser) {
				imageUrl = this.systemUser.pictureUrl ? this.systemUser.pictureUrl : "images/blank_user.png"
			} else {
				if (this.me.pictureUrl) {
					imageUrl = this.me.pictureUrl!
				} else {
					imageUrl = api.pictureUrl(this.me.id)
				}
			}
		}

		return `${imageUrl}?p=${this.pictureGuid}`
	}

	private get otherTenants (): LoginResultTenant[] {
		if (!this.me || !this.me.loginTenants) {
			return []
		}

		return this.me.loginTenants.filter(t => !t.disabled && t.id !== this.me.tenantId)
	}

	get infoEnabled (): boolean {
		return this.me && !!this.me.hasTenantInfo && this.tenantEnabledFeatures["tenantInfo"]
	}

	get userUpdatesFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures["userUpdates"]
	}

	public mounted () {
		this.$root.$on("refresh-profile-picture", this.refreshProfilePicture)
	}

	public beforeDestroy () {
		this.$root.$off("refresh-profile-picture", this.refreshProfilePicture)
	}

	public async logout () {
		this.setManualLogout(true)

		await api.logout()
		this.logOut()
	}

	public toMyAccount () {
		this.$router.push({ path: `/userprofile/${this.me.id}` })
	}

	public showUserSettings () {
		this.$refs.userSettingsModal.open()
	}

	public showSetStatus () {
		this.$refs.setStatusModal.open()
	}

	public toOrganization () {
		this.$router.push({ path: "/organization" })
	}

	@Emit("menuOpen")
	public menuSelected () {
	}

	public async fetchOptions (search: string, loading: (isLoading: boolean) => void) {
		if (this.debounceSearch) {
			clearTimeout(this.debounceSearch)
			this.debounceSearch = null
			loading(false)
		}
		if (search === "") {
			this.searchResults = []
		} else {
			loading(true)
			this.debounceSearch = setTimeout(async () => {
				this.searchResults = [
					{
						itemType: "SEARCH",
						title: "Advanced search for \"" + search + "\"",
						search: search
					},
					...await api.allSearch(search)
				]
				loading(false)
			}, 500)
		}
	}

	public pictureUrl (searchResult: any): string {
		if (searchResult.iconUrl) {
			return searchResult.iconUrl!
		} else {
			return api.pictureUrl(searchResult.id)
		}
	}

	public goToSearchResult (result: any): void {
		if (result.itemType === "RECORD") {
			this.$router.push({ path: `/cases/${result.pageArgument}` })
		} else if (result.itemType === "USER") {
			this.$router.push({ path: `/userprofile/${result.id}` })
		} else if (result.itemType === "SEARCH") {
			this.setSearch(result.search)
			this.$router.push({ path: "/search" })
		}
	}

	public loadedImage (result: any): void {

	}

	public imageUrlError (args: any): void {
		this.pictureError = true
	}

	public theme (newTheme: string): void {
		this.setTheme(newTheme)
	}

	public enableBrowserNotifications (): void {
		this.requestBrowserNotificationsPermission()
	}

	public get browserNotificationsPermissionEnabled (): boolean {
		return this.browserNotificationsEnabled
	}

	public notificationSelected (notification: CasesNotification) {
		this.processNotification(notification)
		this.$refs.notificationsDropdown.hide(true)
	}

	public notificationTime (notification: CasesNotification): string {
		const notificationTime = new Date(moment(notification.created).format("l LTS"))
		const timeDifference = Date.now() - notificationTime.getTime()

		let days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
		let hours = Math.floor(Math.abs(timeDifference) / (1000 * 60 * 60) % 24)
		let minutes = Math.floor(Math.abs(timeDifference) / (1000 * 60) % 60)
		let seconds = Math.ceil(Math.abs(timeDifference) / (1000) % 60)

		let timeMessage = ""
		if (days === 1) {
			timeMessage = `${days} day`
		} else if (days > 1) {
			timeMessage = `${days} days`
		} else if (hours === 1) {
			timeMessage = `${hours} hour`
		} else if (hours > 1) {
			timeMessage = `${hours} hours`
		} else if (minutes === 1) {
			timeMessage = `${minutes} minute`
		} else if (minutes > 1) {
			timeMessage = `${minutes} minutes`
		} else if (seconds === 1) {
			timeMessage = `${seconds} second`
		} else if (seconds > 1) {
			timeMessage = `${seconds} seconds`
		}

		return `${timeMessage} ago`
	}

	public notificationListOpened () {
		this.updateNotificationTimePeriods()
	}

	public notificationListClosing () {
		this.markLocalNotificationsAsSent()
	}

	private updateNotificationTimePeriods () {
		this.notificationTimePeriods = []
		let notifications: CasesNotification[] = this.currentNotifications
		notifications.forEach((notification) => {
			this.notificationTimePeriods.push(this.notificationTime(notification))
		})
	}

	private refreshProfilePicture (): void {
		this.pictureGuid = Guid.newGuid()
	}

	/**
	 * Only show new interface switch if the features are enabled
	 * @private
	 */
	private get showNewInterfaceSwitch () {
		return (this.me?.tenantEnabledFeatures?.includes(TenantFeatures.EnableV2) && (this.me?.isAdmin || this.me?.tenantEnabledFeatures?.includes(TenantFeatures.NewUIOptIn)))
	}

	private requestUpdatesModal () {
		this.$emit("showUpdates", true)
	}

	isNewNotification (notification: any): boolean {
		return !notification.sent ?? true
	}

	clearAllNotifications () {
		if (this.clearAllNotificationsFeatureEnabled) {
			this.clearUserNotifications()
		}
	}

	get clearAllNotificationsFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures[TenantFeatures.NotificationsClearAllButton]
	}

	urgencyText (urgency: Urgency | null): string | null {
		return notificationFunctions.urgencyToString(urgency)
	}
}
